import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  CircularProgress,
  Tooltip,
  Snackbar
} from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OfflineIcon,
  Save as SaveIcon,
  LinkOff as UnassignIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1)
  },
  summary: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  summaryChip: {
    fontWeight: 600
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center'
  },
  searchField: {
    flex: 1
  },
  tableWrap: {
    maxHeight: '60vh'
  },
  deviceName: {
    fontWeight: 600
  },
  small: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  online: {
    color: theme.palette.success.main
  },
  offline: {
    color: theme.palette.error.main
  },
  assignCell: {
    minWidth: 280
  },
  dirtyRow: {
    backgroundColor: 'rgba(255, 215, 0, 0.08)'
  }
}));

const formatLastComm = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const GeotabDevicesModal = (props) => {
  const { open, onClose, url } = props;
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, online: 0, assigned: 0 });
  const [equipmentList, setEquipmentList] = useState([]);
  const [search, setSearch] = useState('');
  const [pendingAssignments, setPendingAssignments] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchAll = async (isRefresh = false, pullLive = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const devicesUrl = pullLive
        ? `${url}/api/equipment/geotab-devices?source=geotab`
        : `${url}/api/equipment/geotab-devices`;
      const [devicesRes, equipRes] = await Promise.all([
        axios.get(devicesUrl),
        axios.get(`${url}/api/equipment/listEquipment`)
      ]);
      setDevices(devicesRes.data.devices || []);
      setSummary({
        total: devicesRes.data.total || 0,
        online: devicesRes.data.online || 0,
        assigned: devicesRes.data.assigned || 0
      });
      const equip = Array.isArray(equipRes.data) ? equipRes.data : (equipRes.data?.equipment || []);
      setEquipmentList(equip);
      setPendingAssignments({});
    } catch (err) {
      console.error('Failed to load Geotab devices:', err);
      setToast({ severity: 'error', message: 'Failed to load Geotab devices' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (open) fetchAll(false);
  }, [open]);

  const filteredDevices = useMemo(() => {
    if (!search.trim()) return devices;
    const q = search.toLowerCase();
    return devices.filter(d => {
      const eqStr = d.equipment
        ? `${d.equipment.name || ''} ${d.equipment.number || ''}`.toLowerCase()
        : '';
      return (
        (d.deviceName || '').toLowerCase().includes(q) ||
        (d.deviceId || '').toLowerCase().includes(q) ||
        (d.serialNumber || '').toLowerCase().includes(q) ||
        (d.vin || '').toLowerCase().includes(q) ||
        eqStr.includes(q)
      );
    });
  }, [devices, search]);

  const handlePick = (device, equipment) => {
    setPendingAssignments(prev => ({
      ...prev,
      [device.deviceId]: equipment || null
    }));
  };

  const isDirty = (device) => {
    if (!(device.deviceId in pendingAssignments)) return false;
    const pending = pendingAssignments[device.deviceId];
    const currentId = device.equipment?._id || null;
    const pendingId = pending?._id || null;
    return currentId !== pendingId;
  };

  const handleSave = async (device) => {
    const pending = pendingAssignments[device.deviceId];
    const equipmentId = pending?._id || null;
    setSavingId(device.deviceId);
    try {
      await axios.post(`${url}/api/equipment/assign-geotab-device`, {
        deviceId: device.deviceId,
        deviceName: device.deviceName,
        equipmentId
      });
      // Apply locally to avoid full refetch.
      setDevices(prev => prev.map(d => {
        if (d.deviceId !== device.deviceId) return d;
        return {
          ...d,
          equipment: pending
            ? {
                _id: pending._id,
                name: pending.name,
                number: pending.number,
                isActive: pending.isActive,
                division: pending.division,
                year: pending.year,
                make: pending.make,
                model: pending.model
              }
            : null
        };
      }));
      // Also clear from any other row that may have shown this equipment (since
      // backend stripped it from any prior owner).
      if (pending) {
        setDevices(prev => prev.map(d => {
          if (d.deviceId === device.deviceId) return d;
          if (d.equipment && d.equipment._id === pending._id) {
            return { ...d, equipment: null };
          }
          return d;
        }));
      }
      setPendingAssignments(prev => {
        const copy = { ...prev };
        delete copy[device.deviceId];
        return copy;
      });
      setToast({
        severity: 'success',
        message: pending
          ? `Assigned ${device.deviceName || device.deviceId} -> ${pending.name || ''} #${pending.number || ''}`
          : `Unassigned ${device.deviceName || device.deviceId}`
      });
    } catch (err) {
      console.error('Save failed:', err);
      setToast({ severity: 'error', message: err.response?.data?.error || 'Save failed' });
    } finally {
      setSavingId(null);
    }
  };

  const renderEquipmentOption = (eq) => (
    <Box>
      <Typography variant="body2">{eq.name} #{eq.number}</Typography>
      <Typography variant="caption" color="textSecondary">
        {[eq.year, eq.make, eq.model].filter(Boolean).join(' ')}
        {eq.division ? ` • ${eq.division}` : ''}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Geotab Devices</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Pull live device list from Geotab (slower; finds unknown devices)">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchAll(true, true)}
                disabled={loading || refreshing}
              >
                Pull from Geotab
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Refresh from local DB">
            <IconButton onClick={() => fetchAll(true, false)} disabled={loading || refreshing}>
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box className={classes.summary}>
          <Chip className={classes.summaryChip} label={`Total: ${summary.total}`} />
          <Chip
            className={classes.summaryChip}
            style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
            icon={<CheckCircleIcon style={{ color: '#2e7d32' }} />}
            label={`Online: ${summary.online}`}
          />
          <Chip
            className={classes.summaryChip}
            style={{ backgroundColor: '#fff3e0', color: '#e65100' }}
            label={`Assigned: ${summary.assigned}`}
          />
          <Chip
            className={classes.summaryChip}
            style={{ backgroundColor: '#fce4ec', color: '#c2185b' }}
            label={`Unassigned: ${summary.total - summary.assigned}`}
          />
        </Box>

        <Box className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            placeholder="Search by device name, ID, VIN, serial, or equipment"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer className={classes.tableWrap}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Last Communication</TableCell>
                  <TableCell className={classes.assignCell}>Assigned Equipment</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDevices.map((device) => {
                  const dirty = isDirty(device);
                  const selected = (device.deviceId in pendingAssignments)
                    ? pendingAssignments[device.deviceId]
                    : (device.equipment
                        ? equipmentList.find(eq => eq._id === device.equipment._id) || device.equipment
                        : null);
                  return (
                    <TableRow
                      key={device.deviceId}
                      className={dirty ? classes.dirtyRow : ''}
                    >
                      <TableCell>
                        {device.isOnline ? (
                          <Tooltip title="Online (comm within 30 min)">
                            <CheckCircleIcon className={classes.online} fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Offline">
                            <OfflineIcon className={classes.offline} fontSize="small" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.deviceName} variant="body2">
                          {device.deviceName || '(unnamed)'}
                        </Typography>
                        <Typography className={classes.small}>
                          ID: {device.deviceId}
                          {device.serialNumber ? ` • SN: ${device.serialNumber}` : ''}
                        </Typography>
                        {device.vin && (
                          <Typography className={classes.small}>VIN: {device.vin}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatLastComm(device.lastCommunication)}</Typography>
                        {device.lastCommunication && (
                          <Typography className={classes.small}>
                            {new Date(device.lastCommunication).toLocaleString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.assignCell}>
                        <Autocomplete
                          size="small"
                          options={equipmentList}
                          value={selected || null}
                          getOptionLabel={(eq) => eq ? `${eq.name || ''} #${eq.number || ''}` : ''}
                          getOptionSelected={(opt, val) => opt._id === val?._id}
                          renderOption={renderEquipmentOption}
                          onChange={(_, val) => handlePick(device, val)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Pick equipment..."
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          {device.equipment && (
                            <Tooltip title="Unassign device">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={savingId === device.deviceId}
                                  onClick={() => {
                                    handlePick(device, null);
                                    // Save immediately after marking unassigned
                                    setTimeout(() => handleSave({ ...device }), 0);
                                  }}
                                >
                                  <UnassignIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={savingId === device.deviceId ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            disabled={!dirty || savingId === device.deviceId}
                            onClick={() => handleSave(device)}
                          >
                            Save
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredDevices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary" style={{ padding: 24 }}>
                        No devices match your search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({ url: state.url });

export default connect(mapStateToProps)(GeotabDevicesModal);
